/**
 * SurveyBuilderPage.tsx
 *
 * Three-panel survey builder matching the old PHP e-form admin:
 *   Left  (w-64) — ordered question list with reorder controls
 *   Center       — question editor with all configurable fields
 *   Right (w-72) — conditional logic rules for the selected question
 *
 * Collapsible survey settings panel at the top.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ChevronUp,
  ChevronDown,
  ChevronRight,
  Plus,
  Trash2,
  Eye,
  Save,
  ArrowLeft,
  Settings,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Select } from '@/components/ui/Select';
import { Spinner } from '@/components/ui/Spinner';
import { toast } from '@/store/toastStore';
import {
  getSurvey,
  updateSurvey,
  addQuestion,
  updateQuestion,
  deleteQuestion,
  reorderQuestions,
  addLogicRule,
  deleteLogicRule,
} from '@/services/surveys.service';
import type {
  SurveyDetail,
  SurveyQuestion,
  SurveyQuestionOption,
  AddLogicRulePayload,
} from '@/services/surveys.service';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Params extends Record<string, string | undefined> {
  id: string;
}

// Pending in-memory edits for a question before saving
interface PendingQuestion {
  questionType: number;
  questionContent: string;
  instructionText: string;
  isRequired: boolean;
  addOther: boolean;
  checkAll: boolean;
  isSummary: boolean;
  recordFieldText: string;
  recordFieldId: number;
  options: Array<{ id?: number; optionText: string; sOrder: number; preselect: boolean }>;
}

// Pending survey settings
interface PendingSurveySettings {
  title: string;
  pageTitle: string;
  headerMessage: string;
  submissionMessage: string;
  submissionUrl: string;
  surveyType: number;
  displayLogo: boolean;
  color1: string;
  color2: string;
  fontColor: string;
  shortcode: string;
  expiresInDays: number | null;
}

interface NewRuleForm {
  targetQuestionId: string;
  logicBoolean: number;
  surveysOptionId: string;
  responseContent: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

// Hardcoded question types matching the old PHP e-form admin
const QUESTION_TYPES: Array<{ value: string; label: string }> = [
  { value: '7',  label: 'Radio Buttons' },
  { value: '8',  label: 'Check Boxes' },
  { value: '9',  label: 'Text Box (multiline)' },
  { value: '10', label: 'Text Line (single line)' },
  { value: '11', label: 'Signature' },
  { value: '12', label: 'File Upload' },
  { value: '13', label: 'Page Break' },
  { value: '14', label: 'Date' },
  { value: '15', label: 'Hidden' },
  { value: '16', label: 'Drop-Down' },
  { value: '17', label: 'BMI Calculator' },
  { value: '18', label: 'Name (first/last)' },
  { value: '19', label: 'Content Box No Scroll' },
  { value: '20', label: 'Submission Button' },
  { value: '21', label: 'Credit Card Entry' },
  { value: '22', label: 'Address (composite)' },
  { value: '23', label: 'Content Box Scroll' },
  { value: '28', label: 'Scroll & Accept' },
];

const QUESTION_TYPE_MAP = new Map(QUESTION_TYPES.map((t) => [Number(t.value), t.label]));

// Question types that support answer options
const OPTIONS_TYPES = new Set([7, 8, 16]); // radio, checkbox, dropdown

// Question types that support "Add Other"
const ADD_OTHER_TYPES = new Set([7, 8, 16]);

// Only checkboxes support "Check All"
const CHECK_ALL_TYPES = new Set([8]);

const FORM_TYPE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '1', label: 'E-Form' },
  { value: '2', label: 'Contact Form' },
];

const YES_NO_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'true',  label: 'Yes' },
  { value: 'false', label: 'No' },
];

const LOGIC_ACTION_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '0', label: 'Do nothing to this field' },
  { value: '1', label: 'Show this field' },
  { value: '2', label: 'Hide this field' },
];

const LOGIC_CONDITION_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '0', label: 'All rules must match' },
  { value: '1', label: 'Any rule must match' },
];

const LOGIC_BOOLEAN_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '0', label: 'is' },
  { value: '1', label: 'is not' },
  { value: '2', label: 'less than' },
  { value: '3', label: 'greater than' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function emptyPending(q: SurveyQuestion): PendingQuestion {
  return {
    questionType:    q.questionType,
    questionContent: q.questionContent,
    instructionText: q.instructionText ?? '',
    isRequired:      q.isRequired,
    addOther:        q.addOther,
    checkAll:        q.checkAll,
    isSummary:       q.isSummary,
    recordFieldText: q.recordFieldText ?? '',
    recordFieldId:   q.recordFieldId ?? 0,
    options:         (q.options ?? []).map((o) => ({ ...o })),
  };
}

function emptySurveySettings(s: SurveyDetail): PendingSurveySettings {
  return {
    title:             s.title ?? '',
    pageTitle:         s.pageTitle ?? '',
    headerMessage:     s.headerMessage ?? '',
    submissionMessage: s.submissionMessage ?? '',
    submissionUrl:     s.submissionUrl ?? '',
    surveyType:        s.surveyType ?? 1,
    displayLogo:       s.displayLogo ?? false,
    color1:            s.color1 ?? '#ffffff',
    color2:            s.color2 ?? '#ffffff',
    fontColor:         s.fontColor ?? '#000000',
    shortcode:         s.shortcode ?? '',
    expiresInDays:     s.expiresInDays,
  };
}

function emptyNewRule(): NewRuleForm {
  return {
    targetQuestionId: '',
    logicBoolean:     0,
    surveysOptionId:  '',
    responseContent:  '',
  };
}

function getTypeLabel(typeId: number): string {
  return QUESTION_TYPE_MAP.get(typeId) ?? `Type ${typeId}`;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function SurveyBuilderPage(): React.ReactElement {
  const { id } = useParams<Params>();
  const navigate = useNavigate();
  const surveyId = Number(id);

  const [survey, setSurvey]             = useState<SurveyDetail | null>(null);
  const [loading, setLoading]           = useState(true);

  // Survey settings
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settings, setSettings]         = useState<PendingSurveySettings | null>(null);
  const [savingSettings, setSavingSettings] = useState(false);

  // Left panel
  const [selectedId, setSelectedId]     = useState<number | null>(null);

  // Center panel — pending edits for selected question
  const [pending, setPending]           = useState<PendingQuestion | null>(null);
  const [savingQ, setSavingQ]           = useState(false);

  // Right panel — logic rules
  const [newRule, setNewRule]           = useState<NewRuleForm>(emptyNewRule());
  const [savingRule, setSavingRule]     = useState(false);

  // ─── Load ──────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!surveyId) return;
    setLoading(true);

    getSurvey(surveyId)
      .then((s) => {
        setSurvey(s);
        setSettings(emptySurveySettings(s));
        if (s.questions.length > 0) {
          const first = s.questions[0];
          setSelectedId(first.id);
          setPending(emptyPending(first));
        }
      })
      .catch(() => toast.error('Failed to load survey'))
      .finally(() => setLoading(false));
  }, [surveyId]);

  const selectedQuestion = survey?.questions.find((q) => q.id === selectedId) ?? null;
  const selectedLogicRules = survey?.logicRules.filter(
    (r) => r.surveysOrderId === selectedId,
  ) ?? [];

  // ─── Select question ───────────────────────────────────────────────────

  const handleSelectQuestion = useCallback(
    (q: SurveyQuestion) => {
      setSelectedId(q.id);
      setPending(emptyPending(q));
      setNewRule(emptyNewRule());
    },
    [],
  );

  // ─── Save survey settings ─────────────────────────────────────────────

  const handleSaveSettings = useCallback(async () => {
    if (!survey || !settings) return;
    setSavingSettings(true);
    try {
      await updateSurvey(survey.id, {
        title:             settings.title,
        pageTitle:         settings.pageTitle || null,
        headerMessage:     settings.headerMessage || null,
        submissionMessage: settings.submissionMessage || null,
        submissionUrl:     settings.submissionUrl || null,
        surveyType:        settings.surveyType,
        displayLogo:       settings.displayLogo,
        color1:            settings.color1 || null,
        color2:            settings.color2 || null,
        fontColor:         settings.fontColor || null,
        shortcode:         settings.shortcode || null,
        expiresInDays:     settings.expiresInDays,
      });
      setSurvey((prev) => prev ? {
        ...prev,
        title:             settings.title,
        pageTitle:         settings.pageTitle || null,
        headerMessage:     settings.headerMessage || null,
        submissionMessage: settings.submissionMessage || null,
        submissionUrl:     settings.submissionUrl || null,
        surveyType:        settings.surveyType,
        displayLogo:       settings.displayLogo,
        color1:            settings.color1 || null,
        color2:            settings.color2 || null,
        fontColor:         settings.fontColor || null,
        shortcode:         settings.shortcode || null,
        expiresInDays:     settings.expiresInDays,
      } : prev);
      toast.success('Survey settings saved');
    } catch {
      toast.error('Failed to save survey settings');
    } finally {
      setSavingSettings(false);
    }
  }, [survey, settings]);

  // ─── Add question ──────────────────────────────────────────────────────

  const handleAddQuestion = useCallback(async () => {
    if (!survey) return;
    try {
      const q = await addQuestion(survey.id, {
        questionContent: 'New question',
        questionType:    10,  // default to Text Line
        isRequired:      false,
        addOther:        false,
        checkAll:        false,
      });
      setSurvey((prev) =>
        prev ? { ...prev, questions: [...prev.questions, q] } : prev,
      );
      setSelectedId(q.id);
      setPending(emptyPending(q));
    } catch {
      toast.error('Failed to add question');
    }
  }, [survey]);

  // ─── Delete question ───────────────────────────────────────────────────

  const handleDeleteQuestion = useCallback(
    async (questionId: number) => {
      if (!survey) return;
      try {
        await deleteQuestion(survey.id, questionId);
        setSurvey((prev) => {
          if (!prev) return prev;
          const remaining = prev.questions.filter((q) => q.id !== questionId);
          const rules     = prev.logicRules.filter(
            (r) => r.surveysOrderId !== questionId,
          );
          return { ...prev, questions: remaining, logicRules: rules };
        });
        if (selectedId === questionId) {
          const remaining = survey.questions.filter((q) => q.id !== questionId);
          const next = remaining[0] ?? null;
          setSelectedId(next?.id ?? null);
          setPending(next ? emptyPending(next) : null);
        }
      } catch {
        toast.error('Failed to delete question');
      }
    },
    [survey, selectedId],
  );

  // ─── Reorder ───────────────────────────────────────────────────────────

  const handleMove = useCallback(
    async (questionId: number, direction: 'up' | 'down') => {
      if (!survey) return;
      const questions = [...survey.questions];
      const idx = questions.findIndex((q) => q.id === questionId);
      if (idx < 0) return;
      const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
      if (swapIdx < 0 || swapIdx >= questions.length) return;

      [questions[idx], questions[swapIdx]] = [questions[swapIdx], questions[idx]];
      setSurvey((prev) => prev ? { ...prev, questions } : prev);

      try {
        const ordered = await reorderQuestions(survey.id, {
          orderedIds: questions.map((q) => q.id),
        });
        setSurvey((prev) => prev ? { ...prev, questions: ordered } : prev);
      } catch {
        toast.error('Failed to reorder questions');
        getSurvey(surveyId).then((s) => setSurvey(s)).catch(() => undefined);
      }
    },
    [survey, surveyId],
  );

  // ─── Save question edits ───────────────────────────────────────────────

  const handleSaveQuestion = useCallback(async () => {
    if (!survey || !selectedId || !pending) return;
    setSavingQ(true);
    try {
      const updated = await updateQuestion(survey.id, selectedId, {
        questionType:    pending.questionType,
        questionContent: pending.questionContent,
        instructionText: pending.instructionText || undefined,
        isRequired:      pending.isRequired,
        addOther:        pending.addOther,
        checkAll:        pending.checkAll,
        isSummary:       pending.isSummary,
        recordFieldText: pending.recordFieldText || null,
        recordFieldId:   pending.recordFieldId || null,
        options:         OPTIONS_TYPES.has(pending.questionType) ? pending.options : undefined,
      });
      setSurvey((prev) =>
        prev
          ? {
              ...prev,
              questions: prev.questions.map((q) => (q.id === updated.id ? updated : q)),
            }
          : prev,
      );
      setPending(emptyPending(updated));
      toast.success('Question saved');
    } catch {
      toast.error('Failed to save question');
    } finally {
      setSavingQ(false);
    }
  }, [survey, selectedId, pending]);

  // ─── Options editor ────────────────────────────────────────────────────

  const handleOptionChange = (index: number, field: keyof SurveyQuestionOption, value: string | number | boolean) => {
    if (!pending) return;
    const opts = [...pending.options];
    opts[index] = { ...opts[index], [field]: value };
    setPending({ ...pending, options: opts });
  };

  const handleAddOption = () => {
    if (!pending) return;
    const nextOrder = pending.options.length > 0
      ? Math.max(...pending.options.map((o) => o.sOrder)) + 1
      : 1;
    setPending({
      ...pending,
      options: [...pending.options, { optionText: '', sOrder: nextOrder, preselect: false }],
    });
  };

  const handleRemoveOption = (index: number) => {
    if (!pending) return;
    const opts = pending.options.filter((_, i) => i !== index);
    setPending({ ...pending, options: opts });
  };

  // ─── Logic rules ───────────────────────────────────────────────────────

  const handleAddRule = useCallback(async () => {
    if (!survey || !selectedId) return;
    const targetId = Number(newRule.targetQuestionId);
    if (!targetId) {
      toast.error('Please select a target question for the rule');
      return;
    }
    setSavingRule(true);
    const payload: AddLogicRulePayload = {
      surveysOrderId: selectedId,
      logicAction:    selectedLogicRules.length > 0 ? selectedLogicRules[0].logicAction : 1,
      logicCondition: selectedLogicRules.length > 0 ? selectedLogicRules[0].logicCondition : 0,
    };
    try {
      const rule = await addLogicRule(survey.id, payload);
      setSurvey((prev) =>
        prev ? { ...prev, logicRules: [...prev.logicRules, rule] } : prev,
      );
      setNewRule(emptyNewRule());
      toast.success('Logic rule added');
    } catch {
      toast.error('Failed to add logic rule');
    } finally {
      setSavingRule(false);
    }
  }, [survey, selectedId, newRule, selectedLogicRules]);

  const handleDeleteRule = useCallback(
    async (ruleId: number) => {
      if (!survey) return;
      try {
        await deleteLogicRule(survey.id, ruleId);
        setSurvey((prev) =>
          prev
            ? { ...prev, logicRules: prev.logicRules.filter((r) => r.id !== ruleId) }
            : prev,
        );
      } catch {
        toast.error('Failed to delete logic rule');
      }
    },
    [survey],
  );

  // ─── Render ────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!survey || !settings) {
    return (
      <div className="p-6 text-sm text-slate-500">Survey not found.</div>
    );
  }

  // Question list for logic rule target dropdowns
  const otherQuestionOptions = survey.questions
    .filter((q) => q.id !== selectedId)
    .map((q) => ({
      value: String(q.id),
      label: q.questionContent || `(Q #${q.id})`,
    }));

  // Get options for a specific question (for rule option dropdowns)
  const getQuestionOptions = (qId: number) => {
    const q = survey.questions.find((x) => x.id === qId);
    return (q?.options ?? []).map((o) => ({
      value: String(o.id),
      label: o.optionText,
    }));
  };

  // Group options (placeholder - groups from survey)
  const groupOptions: Array<{ value: string; label: string }> = [
    { value: '0', label: 'None' },
  ];

  return (
    <div className="flex h-full flex-col">
      {/* ── Top bar ─────────────────────────────────────────────────── */}
      <div className="flex items-center gap-3 border-b border-slate-200 bg-white px-4 py-3">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="mr-1 rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          aria-label="Back"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>

        <span className="flex-1 text-lg font-semibold text-slate-800 truncate">
          {settings.title || 'Untitled Survey'}
        </span>

        <Button
          variant="secondary"
          size="sm"
          onClick={() => setSettingsOpen((o) => !o)}
        >
          <Settings className="mr-1.5 h-4 w-4" />
          Settings
        </Button>

        <Button
          variant="secondary"
          size="sm"
          onClick={() => window.open(`/survey-preview/${survey.id}`, '_blank')}
        >
          <Eye className="mr-1.5 h-4 w-4" />
          Preview
        </Button>
      </div>

      {/* ── Collapsible Survey Settings ─────────────────────────────── */}
      {settingsOpen && (
        <div className="border-b border-slate-200 bg-slate-50 px-6 py-4">
          <h3 className="mb-4 text-sm font-semibold text-slate-700 uppercase tracking-wide">
            Survey Settings
          </h3>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {/* Form Name */}
            <Input
              label="Form Name"
              value={settings.title}
              onChange={(e) => setSettings({ ...settings, title: e.target.value })}
            />
            {/* Page Title */}
            <Input
              label="Page Title"
              value={settings.pageTitle}
              onChange={(e) => setSettings({ ...settings, pageTitle: e.target.value })}
            />
            {/* Form Type */}
            <Select
              label="Form Type"
              options={FORM_TYPE_OPTIONS}
              value={String(settings.surveyType)}
              onChange={(v) => v && setSettings({ ...settings, surveyType: Number(v) })}
            />
            {/* Submission URL */}
            <Input
              label="Submission URL"
              value={settings.submissionUrl}
              onChange={(e) => setSettings({ ...settings, submissionUrl: e.target.value })}
              placeholder="https://..."
            />
            {/* Display Logo */}
            <Select
              label="Display Logo"
              options={YES_NO_OPTIONS}
              value={String(settings.displayLogo)}
              onChange={(v) => v && setSettings({ ...settings, displayLogo: v === 'true' })}
            />
            {/* Expires In (days) */}
            <Input
              label="Expires In (days)"
              type="number"
              value={settings.expiresInDays !== null ? String(settings.expiresInDays) : ''}
              onChange={(e) => setSettings({
                ...settings,
                expiresInDays: e.target.value ? Number(e.target.value) : null,
              })}
              placeholder="e.g. 30"
            />
            {/* Color 1 */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Color 1</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={settings.color1}
                  onChange={(e) => setSettings({ ...settings, color1: e.target.value })}
                  className="h-9 w-12 cursor-pointer rounded border border-slate-300"
                />
                <input
                  type="text"
                  value={settings.color1}
                  onChange={(e) => setSettings({ ...settings, color1: e.target.value })}
                  className="form-input-base flex-1"
                />
              </div>
            </div>
            {/* Color 2 */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Color 2</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={settings.color2}
                  onChange={(e) => setSettings({ ...settings, color2: e.target.value })}
                  className="h-9 w-12 cursor-pointer rounded border border-slate-300"
                />
                <input
                  type="text"
                  value={settings.color2}
                  onChange={(e) => setSettings({ ...settings, color2: e.target.value })}
                  className="form-input-base flex-1"
                />
              </div>
            </div>
            {/* Font Color */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Font Color</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={settings.fontColor}
                  onChange={(e) => setSettings({ ...settings, fontColor: e.target.value })}
                  className="h-9 w-12 cursor-pointer rounded border border-slate-300"
                />
                <input
                  type="text"
                  value={settings.fontColor}
                  onChange={(e) => setSettings({ ...settings, fontColor: e.target.value })}
                  className="form-input-base flex-1"
                />
              </div>
            </div>
            {/* Shortcode (read-only) */}
            <Input
              label="Shortcode"
              value={settings.shortcode}
              readOnly
              hint="Auto-generated shortcode (read-only)"
            />
          </div>

          {/* Header Message */}
          <div className="mt-4">
            <Textarea
              label="Header Message"
              value={settings.headerMessage}
              onChange={(e) => setSettings({ ...settings, headerMessage: e.target.value })}
              rows={3}
              placeholder="Message displayed at the top of the form"
            />
          </div>

          {/* Submission Message */}
          <div className="mt-4">
            <Textarea
              label="Submission Message"
              value={settings.submissionMessage}
              onChange={(e) => setSettings({ ...settings, submissionMessage: e.target.value })}
              rows={3}
              placeholder="Message shown after form submission"
            />
          </div>

          <div className="mt-4 flex justify-end">
            <Button
              variant="primary"
              size="sm"
              onClick={() => void handleSaveSettings()}
              loading={savingSettings}
            >
              <Save className="mr-1.5 h-4 w-4" />
              Save Settings
            </Button>
          </div>
        </div>
      )}

      {/* ── Three-column body ────────────────────────────────────────── */}
      <div className="flex flex-1 overflow-hidden">

        {/* ── LEFT: question list ──────────────────────────────────── */}
        <aside className="flex w-64 flex-shrink-0 flex-col border-r border-slate-200 bg-slate-50">
          <div className="flex-1 overflow-y-auto p-3">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
              Questions ({survey.questions.length})
            </p>
            {survey.questions.length === 0 && (
              <p className="text-xs text-slate-400">No questions yet.</p>
            )}
            <ul className="space-y-1">
              {survey.questions.map((q, idx) => (
                <li
                  key={q.id}
                  className={`group flex items-start gap-1 rounded-md border px-2 py-2 text-sm cursor-pointer
                    ${selectedId === q.id
                      ? 'border-blue-300 bg-blue-50 text-blue-800'
                      : 'border-transparent bg-white text-slate-700 hover:border-slate-200 hover:bg-white'
                    }`}
                  onClick={() => handleSelectQuestion(q)}
                >
                  {/* Reorder arrows */}
                  <div className="mt-0.5 flex flex-col">
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); void handleMove(q.id, 'up'); }}
                      disabled={idx === 0}
                      className="rounded p-0.5 text-slate-300 hover:bg-slate-100 hover:text-slate-600 disabled:opacity-0"
                      aria-label="Move question up"
                    >
                      <ChevronUp className="h-3 w-3" />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); void handleMove(q.id, 'down'); }}
                      disabled={idx === survey.questions.length - 1}
                      className="rounded p-0.5 text-slate-300 hover:bg-slate-100 hover:text-slate-600 disabled:opacity-0"
                      aria-label="Move question down"
                    >
                      <ChevronDown className="h-3 w-3" />
                    </button>
                  </div>

                  {/* Order number + label */}
                  <span className="flex-shrink-0 text-xs font-mono text-slate-400 mt-0.5">
                    {idx + 1}.
                  </span>
                  <div className="flex-1 min-w-0">
                    <span className="block truncate">{q.questionContent || '(Untitled)'}</span>
                    <span className="text-[10px] text-slate-400">{getTypeLabel(q.questionType)}</span>
                  </div>

                  {/* Delete */}
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); void handleDeleteQuestion(q.id); }}
                    className="ml-auto hidden flex-shrink-0 rounded p-0.5 text-slate-300 hover:text-red-500 group-hover:block"
                    aria-label="Delete question"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Add question button */}
          <div className="border-t border-slate-200 p-3">
            <button
              type="button"
              onClick={() => void handleAddQuestion()}
              className="flex w-full items-center justify-center gap-1.5 rounded-md border border-dashed border-slate-300
                         px-3 py-2 text-sm text-slate-500 hover:border-blue-400 hover:text-blue-600"
            >
              <Plus className="h-4 w-4" />
              Add Question
            </button>
          </div>
        </aside>

        {/* ── CENTER: question editor ──────────────────────────────── */}
        <main className="flex-1 overflow-y-auto bg-white p-6">
          {!selectedQuestion || !pending ? (
            <div className="flex h-full items-center justify-center text-sm text-slate-400">
              Select or add a question to edit it.
            </div>
          ) : (
            <div className="mx-auto max-w-2xl space-y-5">
              <div className="flex items-center justify-between">
                <h2 className="text-base font-semibold text-slate-700">
                  Edit Question #{survey.questions.findIndex((q) => q.id === selectedId) + 1}
                </h2>
                <span className="text-xs text-slate-400">ID: {selectedQuestion.id}</span>
              </div>

              {/* Question Content */}
              <Textarea
                label="Question Content"
                value={pending.questionContent}
                onChange={(e) => setPending({ ...pending, questionContent: e.target.value })}
                placeholder="Enter your question text..."
                rows={3}
              />

              {/* Instruction Text */}
              <Textarea
                label="Instruction Text"
                value={pending.instructionText}
                onChange={(e) => setPending({ ...pending, instructionText: e.target.value })}
                placeholder="Help text displayed below the question"
                rows={2}
                hint="Optional helper text shown below the question"
              />

              {/* Type */}
              <Select
                label="Type"
                options={QUESTION_TYPES}
                value={String(pending.questionType)}
                onChange={(v) => v && setPending({ ...pending, questionType: Number(v) })}
              />

              {/* Options (for radio/checkbox/dropdown) */}
              {OPTIONS_TYPES.has(pending.questionType) && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Options
                  </label>
                  <div className="space-y-2 rounded-md border border-slate-200 bg-slate-50 p-3">
                    {pending.options.length === 0 && (
                      <p className="text-xs text-slate-400">No options yet. Add one below.</p>
                    )}
                    {pending.options.map((opt, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <span className="text-xs text-slate-400 w-6 text-right flex-shrink-0">{opt.sOrder}.</span>
                        <input
                          type="text"
                          value={opt.optionText}
                          onChange={(e) => handleOptionChange(i, 'optionText', e.target.value)}
                          placeholder={`Option ${i + 1}`}
                          className="flex-1 rounded-md border border-slate-300 px-3 py-1.5 text-sm
                                     focus:outline-none focus:ring-2 focus:ring-blue-400"
                        />
                        <label className="flex items-center gap-1 text-xs text-slate-500 flex-shrink-0">
                          <input
                            type="checkbox"
                            checked={opt.preselect}
                            onChange={(e) => handleOptionChange(i, 'preselect', e.target.checked)}
                            className="h-3 w-3 rounded border-slate-300 text-blue-600"
                          />
                          Pre-select
                        </label>
                        <button
                          type="button"
                          onClick={() => handleRemoveOption(i)}
                          className="text-slate-300 hover:text-red-500 flex-shrink-0"
                          aria-label="Remove option"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={handleAddOption}
                      className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 mt-1"
                    >
                      <Plus className="h-3.5 w-3.5" />
                      Add option
                    </button>
                  </div>
                </div>
              )}

              {/* Toggles row */}
              <div className="grid grid-cols-2 gap-4">
                {/* Add "Other" — for radio, checkbox, dropdown */}
                {ADD_OTHER_TYPES.has(pending.questionType) && (
                  <Select
                    label='Add "Other"'
                    options={YES_NO_OPTIONS}
                    value={String(pending.addOther)}
                    onChange={(v) => v && setPending({ ...pending, addOther: v === 'true' })}
                  />
                )}

                {/* Check All — checkbox only */}
                {CHECK_ALL_TYPES.has(pending.questionType) && (
                  <Select
                    label='Add "Check All"'
                    options={YES_NO_OPTIONS}
                    value={String(pending.checkAll)}
                    onChange={(v) => v && setPending({ ...pending, checkAll: v === 'true' })}
                  />
                )}

                {/* Required */}
                <Select
                  label="Required"
                  options={YES_NO_OPTIONS}
                  value={String(pending.isRequired)}
                  onChange={(v) => v && setPending({ ...pending, isRequired: v === 'true' })}
                />

                {/* Summary */}
                <Select
                  label="Summary"
                  options={YES_NO_OPTIONS}
                  value={String(pending.isSummary)}
                  onChange={(v) => v && setPending({ ...pending, isSummary: v === 'true' })}
                />
              </div>

              {/* Question Group */}
              <Select
                label="Question Group"
                options={groupOptions}
                value="0"
                onChange={() => {}}
                hint="Group assignment for this question"
              />

              {/* Populates (record field) */}
              <Input
                label="Populates (Record Field)"
                value={pending.recordFieldText}
                onChange={(e) => setPending({ ...pending, recordFieldText: e.target.value })}
                placeholder="e.g. patient_email, patient_phone"
                hint="Links this answer to a patient record field"
              />

              {/* Save button */}
              <div className="pt-2 border-t border-slate-200">
                <Button
                  variant="primary"
                  onClick={() => void handleSaveQuestion()}
                  loading={savingQ}
                >
                  <Save className="mr-1.5 h-4 w-4" />
                  Save Question
                </Button>
              </div>
            </div>
          )}
        </main>

        {/* ── RIGHT: logic rules ───────────────────────────────────── */}
        <aside className="flex w-72 flex-shrink-0 flex-col border-l border-slate-200 bg-slate-50">
          <div className="flex-1 overflow-y-auto p-3">
            <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-400">
              Logic Rules
            </p>

            {!selectedQuestion ? (
              <p className="text-xs text-slate-400">Select a question to manage logic.</p>
            ) : (
              <>
                {/* Logic Action for this question */}
                {selectedLogicRules.length > 0 && (
                  <div className="mb-3 space-y-2 rounded-md border border-slate-200 bg-white p-2">
                    <p className="text-[10px] font-semibold uppercase text-slate-400">
                      When conditions are met:
                    </p>
                    <div className="text-xs text-slate-600">
                      <strong>Action:</strong>{' '}
                      {LOGIC_ACTION_OPTIONS.find(
                        (o) => Number(o.value) === selectedLogicRules[0]?.logicAction,
                      )?.label ?? 'Unknown'}
                    </div>
                    <div className="text-xs text-slate-600">
                      <strong>Condition:</strong>{' '}
                      {LOGIC_CONDITION_OPTIONS.find(
                        (o) => Number(o.value) === selectedLogicRules[0]?.logicCondition,
                      )?.label ?? 'Unknown'}
                    </div>
                  </div>
                )}

                {/* Existing rules */}
                {selectedLogicRules.length === 0 ? (
                  <p className="mb-3 text-xs text-slate-400">No rules for this question.</p>
                ) : (
                  <ul className="mb-3 space-y-2">
                    {selectedLogicRules.map((rule) => (
                      <li
                        key={rule.id}
                        className="rounded-md border border-slate-200 bg-white p-2 text-xs text-slate-600"
                      >
                        <div className="mb-1 flex items-start justify-between gap-1">
                          <span className="font-medium">
                            Rule #{rule.id}
                          </span>
                          <button
                            type="button"
                            onClick={() => void handleDeleteRule(rule.id)}
                            className="flex-shrink-0 text-slate-300 hover:text-red-500"
                            aria-label="Delete rule"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>

                        {/* Sub-rules */}
                        {rule.rules.length > 0 ? (
                          <ul className="mt-1 space-y-1 border-t border-slate-100 pt-1">
                            {rule.rules.map((sub) => {
                              const targetQ = survey.questions.find((q) => q.id === sub.targetItemId);
                              const boolLabel = LOGIC_BOOLEAN_OPTIONS.find(
                                (o) => Number(o.value) === sub.logicBoolean,
                              )?.label ?? '?';
                              const optLabel = targetQ?.options?.find(
                                (o) => o.id === sub.surveysOptionId,
                              )?.optionText;
                              return (
                                <li key={sub.id} className="text-[11px] text-slate-500">
                                  <ChevronRight className="inline h-3 w-3 text-slate-300" />
                                  {' '}
                                  <strong>{targetQ?.questionContent ?? `Q#${sub.targetItemId}`}</strong>
                                  {' '}<em>{boolLabel}</em>{' '}
                                  {optLabel ? `"${optLabel}"` : ''}
                                  {sub.responseContent ? `"${sub.responseContent}"` : ''}
                                </li>
                              );
                            })}
                          </ul>
                        ) : (
                          <p className="text-[10px] text-slate-400 mt-1">No sub-rules defined.</p>
                        )}
                      </li>
                    ))}
                  </ul>
                )}

                {/* Add new rule form */}
                <div className="space-y-2 rounded-md border border-blue-200 bg-blue-50 p-2">
                  <p className="text-xs font-medium text-blue-700">Add Rule</p>

                  <Select
                    label="Target Question"
                    options={otherQuestionOptions}
                    value={newRule.targetQuestionId}
                    onChange={(v) => setNewRule({ ...newRule, targetQuestionId: v ?? '', surveysOptionId: '' })}
                    placeholder="Select question..."
                  />

                  <Select
                    label="Operator"
                    options={LOGIC_BOOLEAN_OPTIONS}
                    value={String(newRule.logicBoolean)}
                    onChange={(v) => v != null && setNewRule({ ...newRule, logicBoolean: Number(v) })}
                  />

                  {/* Option dropdown — if target question has options */}
                  {newRule.targetQuestionId && getQuestionOptions(Number(newRule.targetQuestionId)).length > 0 && (
                    <Select
                      label="Option"
                      options={getQuestionOptions(Number(newRule.targetQuestionId))}
                      value={newRule.surveysOptionId}
                      onChange={(v) => setNewRule({ ...newRule, surveysOptionId: v ?? '' })}
                      placeholder="Select option..."
                      clearable
                    />
                  )}

                  {/* Response Content — text matching */}
                  <Input
                    label="Response Content"
                    value={newRule.responseContent}
                    onChange={(e) => setNewRule({ ...newRule, responseContent: e.target.value })}
                    placeholder="Text to match..."
                  />

                  <Button
                    size="sm"
                    variant="primary"
                    onClick={() => void handleAddRule()}
                    loading={savingRule}
                    className="w-full"
                  >
                    <Plus className="mr-1 h-3.5 w-3.5" />
                    Add Rule
                  </Button>
                </div>
              </>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
